import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftRight,
  CheckCircle2,
  ClipboardCopy,
  Clock,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import {
  decodeUnixTime,
  encodeUnixTime,
  type UnixUnit,
} from '../lib/unixTime';
import { ToolHeader } from '../components/ToolHeader';

/**
 * `/tools/convert/unix-time` — Unix epoch ↔ human time, local + UTC only.
 *
 * Layout:
 *   1. "Now" strip — live-updating ms / s tickers (lets users sanity-
 *      check the current time without copy-pasting from `date +%s`).
 *   2. Timestamp → time pane with auto / sec / ms unit toggle.
 *   3. Time → timestamp pane with `<input type="datetime-local">` and
 *      a `local | UTC` zone select.
 */
export default function UnixTimeTool() {
  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <ToolHeader
        toolId="unix-time"
        category="convert"
        description="时间戳 ↔ ISO 8601 / UTC / 本地时间。10 位 = 秒，13 位 = 毫秒。全部在浏览器内运算，不会上传任何数据。"
      />

      <NowStrip />
      <TimestampToTime />
      <TimeToTimestamp />
    </div>
  );
}

function NowStrip(): JSX.Element {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const ms = now;
  const s = Math.floor(now / 1000);
  return (
    <div className="card-pad flex flex-wrap items-center gap-3 text-sm">
      <Clock className="h-4 w-4 text-accent" />
      <span className="text-ink-muted">现在</span>
      <CopyValue label="秒" value={String(s)} />
      <CopyValue label="毫秒" value={String(ms)} />
      <span className="ml-auto font-mono text-[11px] text-ink-dim">
        {new Date(now).toISOString()}
      </span>
    </div>
  );
}

function TimestampToTime(): JSX.Element {
  const [input, setInput] = useState('');
  const [unit, setUnit] = useState<UnixUnit>('auto');
  const result = useMemo(() => decodeUnixTime(input, unit), [input, unit]);

  return (
    <section className="card-pad flex flex-col gap-2">
      <header className="flex items-center gap-2 text-sm font-medium text-ink">
        <ArrowLeftRight className="h-4 w-4" />
        时间戳 → 时间
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="input flex-1 font-mono"
          placeholder="例如 1748783700 / 1748783700000"
          value={input}
          onChange={(e) => setInput(e.target.value)}
        />
        <label className="flex items-center gap-1 text-xs text-ink-muted">
          单位
          <select
            className="select py-1"
            value={unit}
            onChange={(e) => setUnit(e.target.value as UnixUnit)}
          >
            <option value="auto">自动 (10/13 位)</option>
            <option value="seconds">秒</option>
            <option value="milliseconds">毫秒</option>
          </select>
        </label>
      </div>

      {input.trim().length === 0 ? (
        <p className="text-[12px] text-ink-dim">— 输入时间戳后这里会显示解析结果 —</p>
      ) : result.ok ? (
        <dl className="grid grid-cols-1 gap-1 text-[12.5px] sm:grid-cols-[7em_1fr]">
          <FieldRow label="解析单位" value={result.data.unit === 'seconds' ? '秒' : '毫秒'} />
          <FieldRow label="UTC" value={result.data.utc} mono />
          <FieldRow label="本地" value={result.data.local} mono />
          <FieldRow label="ISO 8601" value={result.data.iso} mono />
          <FieldRow label="相对" value={result.data.relative} />
        </dl>
      ) : (
        <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
          <XCircle className="h-3.5 w-3.5" />
          {result.error}
        </p>
      )}
    </section>
  );
}

function TimeToTimestamp(): JSX.Element {
  const [human, setHuman] = useState('');
  const [tz, setTz] = useState<'local' | 'utc'>('local');
  const result = useMemo(() => encodeUnixTime(human, tz), [human, tz]);

  return (
    <section className="card-pad flex flex-col gap-2">
      <header className="flex items-center gap-2 text-sm font-medium text-ink">
        <ArrowLeftRight className="h-4 w-4 rotate-180" />
        时间 → 时间戳
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <input
          className="input flex-1 font-mono"
          type="datetime-local"
          step={1}
          value={human}
          onChange={(e) => setHuman(e.target.value)}
        />
        <label className="flex items-center gap-1 text-xs text-ink-muted">
          时区
          <select
            className="select py-1"
            value={tz}
            onChange={(e) => setTz(e.target.value as 'local' | 'utc')}
          >
            <option value="local">本地</option>
            <option value="utc">UTC</option>
          </select>
        </label>
      </div>

      {human.trim().length === 0 ? (
        <p className="text-[12px] text-ink-dim">— 选择一个日期时间后这里会显示对应时间戳 —</p>
      ) : result.ok ? (
        <div className="grid grid-cols-1 gap-1 text-[12.5px] sm:grid-cols-[7em_1fr]">
          <FieldRow label="秒" value={String(result.seconds)} mono copy />
          <FieldRow label="毫秒" value={String(result.milliseconds)} mono copy />
        </div>
      ) : (
        <p className="inline-flex items-center gap-1 text-sm text-rose-600 dark:text-rose-400">
          <XCircle className="h-3.5 w-3.5" />
          {result.error}
        </p>
      )}
    </section>
  );
}

function FieldRow({
  label,
  value,
  mono,
  copy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copy?: boolean;
}): JSX.Element {
  return (
    <>
      <dt className="text-[11px] uppercase tracking-wide text-ink-dim sm:pt-0.5">{label}</dt>
      <dd className={clsx('flex items-center gap-2', mono && 'font-mono')}>
        <span className="min-w-0 break-all text-ink">{value}</span>
        {copy && <CopyButton value={value} />}
      </dd>
    </>
  );
}

function CopyValue({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-bg-soft px-1.5 py-0.5 font-mono text-xs">
      <span className="text-ink-muted">{label}</span>
      <span className="text-ink">{value}</span>
      <CopyButton value={value} compact />
    </span>
  );
}

function CopyButton({ value, compact }: { value: string; compact?: boolean }): JSX.Element {
  const [state, setState] = useState<'idle' | 'ok' | 'err'>('idle');
  const onClick = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setState('ok');
      window.setTimeout(() => setState('idle'), 1000);
    } catch {
      setState('err');
      window.setTimeout(() => setState('idle'), 1500);
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center rounded text-ink-dim hover:bg-bg-soft hover:text-accent transition',
        compact ? 'p-0.5' : 'p-1',
      )}
      title="复制"
    >
      {state === 'ok' ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-500" />
      ) : (
        <ClipboardCopy className="h-3 w-3" />
      )}
    </button>
  );
}
